/**
 * MapboxMiniMap — lightweight Mapbox GL map for inline/preview use.
 *
 * Drop-in replacement for Leaflet MapContainer in:
 * - CohortsPage (cohort overview maps)
 * - FarmerProfilePage (field boundary preview)
 * - TraceabilityPage (TreeLocationPicker)
 * - MiniMap.tsx (dashboard overview)
 *
 * Supports: polygons, markers, click events, auto-fit bounds.
 *
 * Lifecycle-safe: data persists across basemap changes and initializes
 * reliably after style load.
 */

import React, { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl-csp';
import 'mapbox-gl/dist/mapbox-gl.css';
import { getMapboxToken, validateMapboxConfig, BASEMAP_STYLES } from '../../features/maps/config/mapboxConfig';

// ─── Types ──────────────────────────────────────────────────────────

interface MapboxMiniMapProps {
    center?: [number, number]; // [lat, lng] — matches Leaflet convention
    zoom?: number;
    style?: React.CSSProperties;
    className?: string;
    scrollWheelZoom?: boolean;

    // Polygons to render
    polygons?: Array<{
        id: string | number;
        coordinates: Array<[number, number]>; // [[lat, lng], ...]
        color?: string;
        fillColor?: string;
        fillOpacity?: number;
        label?: string;
        onClick?: () => void;
    }>;

    // Markers to render
    markers?: Array<{
        id: string | number;
        position: [number, number]; // [lat, lng]
        color?: string;
        label?: string;
        onClick?: () => void;
    }>;

    // Basemap
    basemap?: 'streets' | 'satellite' | 'terrain';

    // Click handler (for drawing/placing)
    onMapClick?: (lat: number, lng: number) => void;

    // Auto-fit to content
    fitBounds?: boolean;

    // Called when map is ready
    onMapReady?: (map: mapboxgl.Map) => void;

    // Cursor style
    cursor?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────

const POLY_SOURCE = 'mini-polygons';
const POLY_FILL = 'mini-poly-fill';
const POLY_LINE = 'mini-poly-line';
const POLY_LABEL = 'mini-poly-label';

const MARK_SOURCE = 'mini-markers';
const MARK_CIRCLE = 'mini-marker-circle';
const MARK_LABEL = 'mini-marker-label';

function ensurePolyLayers(map: mapboxgl.Map) {
    if (map.getSource(POLY_SOURCE)) return;
    map.addSource(POLY_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: POLY_FILL, type: 'fill', source: POLY_SOURCE, paint: { 'fill-opacity': 0.2 } });
    map.addLayer({ id: POLY_LINE, type: 'line', source: POLY_SOURCE, paint: { 'line-width': 2.5 } });
    map.addLayer({
        id: POLY_LABEL, type: 'symbol', source: POLY_SOURCE,
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'] },
        paint: { 'text-color': '#1E40AF', 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
    });
}

function ensureMarkLayers(map: mapboxgl.Map) {
    if (map.getSource(MARK_SOURCE)) return;
    map.addSource(MARK_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({ id: MARK_CIRCLE, type: 'circle', source: MARK_SOURCE, paint: { 'circle-radius': 8, 'circle-stroke-width': 3, 'circle-stroke-color': '#fff' } });
    map.addLayer({
        id: MARK_LABEL, type: 'symbol', source: MARK_SOURCE,
        layout: { 'text-field': ['get', 'label'], 'text-size': 11, 'text-offset': [0, 1.5] },
        paint: { 'text-color': '#374151', 'text-halo-color': '#fff', 'text-halo-width': 1 },
    });
}

function polyFeatures(polygons: MapboxMiniMapProps['polygons'] = []) {
    return polygons.map(p => {
        const coords = p.coordinates.map(([lat, lng]) => [lng, lat]);
        if (coords.length >= 3) coords.push(coords[0]); // close ring
        return {
            type: 'Feature' as const,
            id: p.id,
            geometry: { type: 'Polygon' as const, coordinates: [coords] },
            properties: { label: p.label || '', _color: p.color || '#10B981', _fillColor: p.fillColor || p.color || '#10B981' },
        };
    });
}

function markFeatures(markers: MapboxMiniMapProps['markers'] = []) {
    return markers.map(m => ({
        type: 'Feature' as const,
        id: m.id,
        geometry: { type: 'Point' as const, coordinates: [m.position[1], m.position[0]] },
        properties: { label: m.label || '', _color: m.color || '#10B981' },
    }));
}

function fitToPolygons(map: mapboxgl.Map, polygons: MapboxMiniMapProps['polygons'] = []) {
    if (polygons.length === 0) return;
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const p of polygons) {
        for (const [lat, lng] of p.coordinates) {
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    }
    if (minLng < Infinity) {
        map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 40, maxZoom: 16, duration: 0 });
    }
}

// ─── Component ──────────────────────────────────────────────────────

const MapboxMiniMap: React.FC<MapboxMiniMapProps> = ({
    center = [-1.9441, 30.0619],
    zoom = 13,
    style = { height: '100%', width: '100%' },
    className = '',
    scrollWheelZoom = false,
    polygons = [],
    markers = [],
    basemap = 'streets',
    onMapClick,
    fitBounds = false,
    onMapReady,
    cursor,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const dataVersionRef = useRef(0); // bumps on every prop change to force re-render after style load
    const initialStyleKey = basemap === 'satellite' ? 'satellite' : basemap === 'terrain' ? 'terrain' : 'streets';
    const appliedStyleRef = useRef(BASEMAP_STYLES[initialStyleKey]);

    // Store latest props in refs so the style-data handler can re-apply them
    const propsRef = useRef({ polygons, markers, fitBounds, onMapClick, cursor, center, zoom });
    propsRef.current = { polygons, markers, fitBounds, onMapClick, cursor, center, zoom };

    // ── Initialize ────────────────────────────────────────
    useEffect(() => {
        if (!containerRef.current) return;

        const config = validateMapboxConfig();
        if (!config.valid) {
            console.error('[MapboxMiniMap]', config.error);
            return;
        }

        mapboxgl.accessToken = getMapboxToken();
        mapboxgl.workerUrl = `${process.env.PUBLIC_URL || ''}/mapbox-gl-csp-worker.js`;

        const styleKey = basemap === 'satellite' ? 'satellite' : basemap === 'terrain' ? 'terrain' : 'streets';

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: BASEMAP_STYLES[styleKey],
            center: [center[1], center[0]], // [lng, lat]
            zoom,
            scrollZoom: scrollWheelZoom,
            attributionControl: false,
        });

        if (cursor) {
            map.getCanvas().style.cursor = cursor;
        }

        // Click handler — always read latest callback from ref
        map.on('click', (e) => {
            propsRef.current.onMapClick?.(e.lngLat.lat, e.lngLat.lng);
        });

        const handlePolygonClick = (e: mapboxgl.MapLayerMouseEvent) => {
            const id = e.features?.[0]?.id;
            const polygon = propsRef.current.polygons.find((item) => String(item.id) === String(id));
            polygon?.onClick?.();
        };
        const handleMarkerClick = (e: mapboxgl.MapLayerMouseEvent) => {
            const id = e.features?.[0]?.id;
            const marker = propsRef.current.markers.find((item) => String(item.id) === String(id));
            marker?.onClick?.();
        };

        // Delegated handlers survive style changes and use the latest props.
        map.on('click', POLY_FILL, handlePolygonClick);
        map.on('click', MARK_CIRCLE, handleMarkerClick);

        // After style loads (initial + basemap changes), restore all data
        const onStyleData = () => {
            if (!map.isStyleLoaded()) return;
            const p = propsRef.current;

            // Re-create layers if they were wiped by setStyle
            ensurePolyLayers(map);
            ensureMarkLayers(map);

            // Re-apply data
            (map.getSource(POLY_SOURCE) as any)?.setData({ type: 'FeatureCollection', features: polyFeatures(p.polygons) });
            (map.getSource(MARK_SOURCE) as any)?.setData({ type: 'FeatureCollection', features: markFeatures(p.markers) });

            // Re-apply paint properties
            if (map.getLayer(POLY_FILL)) {
                map.setPaintProperty(POLY_FILL, 'fill-color', ['coalesce', ['get', '_fillColor'], '#10B981']);
                map.setPaintProperty(POLY_FILL, 'fill-opacity', p.polygons[0]?.fillOpacity ?? 0.2);
            }
            if (map.getLayer(POLY_LINE)) {
                map.setPaintProperty(POLY_LINE, 'line-color', ['coalesce', ['get', '_color'], '#10B981']);
            }
            if (map.getLayer(MARK_CIRCLE)) {
                map.setPaintProperty(MARK_CIRCLE, 'circle-color', ['coalesce', ['get', '_color'], '#10B981']);
            }

            // Fit bounds
            if (p.fitBounds) fitToPolygons(map, p.polygons);

            // Cursor
            if (p.cursor) map.getCanvas().style.cursor = p.cursor;
        };

        map.on('style.load', onStyleData);

        map.on('load', () => {
            onStyleData();
            onMapReady?.(map);
        });

        mapRef.current = map;

        return () => {
            map.off('style.load', onStyleData);
            map.off('click', POLY_FILL, handlePolygonClick);
            map.off('click', MARK_CIRCLE, handleMarkerClick);
            map.remove();
            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Re-apply data when props change (polygons/markers/fitBounds) ──
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) return;

        ensurePolyLayers(map);
        ensureMarkLayers(map);

        (map.getSource(POLY_SOURCE) as any)?.setData({ type: 'FeatureCollection', features: polyFeatures(polygons) });
        (map.getSource(MARK_SOURCE) as any)?.setData({ type: 'FeatureCollection', features: markFeatures(markers) });

        if (map.getLayer(POLY_FILL)) {
            map.setPaintProperty(POLY_FILL, 'fill-color', ['coalesce', ['get', '_fillColor'], '#10B981']);
            map.setPaintProperty(POLY_FILL, 'fill-opacity', polygons[0]?.fillOpacity ?? 0.2);
        }
        if (map.getLayer(POLY_LINE)) {
            map.setPaintProperty(POLY_LINE, 'line-color', ['coalesce', ['get', '_color'], '#10B981']);
        }
        if (map.getLayer(MARK_CIRCLE)) {
            map.setPaintProperty(MARK_CIRCLE, 'circle-color', ['coalesce', ['get', '_color'], '#10B981']);
        }

        if (fitBounds) fitToPolygons(map, polygons);

        // Store version so the style-data handler knows data was applied
        dataVersionRef.current++;
    }, [polygons, markers, fitBounds]);

    // ── Basemap change ────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        const styleKey = basemap === 'satellite' ? 'satellite' : basemap === 'terrain' ? 'terrain' : 'streets';
        const target = BASEMAP_STYLES[styleKey];
        if (appliedStyleRef.current === target) return;
        map.setStyle(target);
        appliedStyleRef.current = target;
        // style.load handler will restore data
    }, [basemap]);

    // ── Center/zoom updates ───────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        map.flyTo({ center: [center[1], center[0]], zoom, duration: 300 });
    }, [center, zoom]);

    // ── Cursor updates ────────────────────────────────────
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !cursor) return;
        map.getCanvas().style.cursor = cursor;
    }, [cursor]);

    return (
        <div ref={containerRef} style={style} className={className} />
    );
};

export default MapboxMiniMap;
